import { isVehicleComplete } from 'cart'
import { I18n, Translate } from 'common'
import { observer } from 'mobx-react-lite'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import Select, {
  DropdownIndicatorProps,
  GroupBase,
  InputActionMeta,
  components,
} from 'react-select'
import { debounce } from 'lodash-es'
import styled from 'styled-components'
import { VehicleSuggestionData } from '../../../../../api/vehicle/interfaces'
import VehicleSaytServiceProvider from '../../../../../services/VehicleSaytServiceProvider'
import VehicleServiceProvider from '../../../../../services/VehicleServiceProvider'
import { StoreInstances } from '../../../../../store/StoreInstancesContainer'
import { usePartsCatalog } from '../../../../../store/partsCatalog/PartsCatalog'
import { useSpecificConditionStore } from '../../../../../store/specificConditions/SpecificConditionsStore'
import { useVehicleWidgetStore } from '../../store/VehicleWidgetStore'
import styles from './index.module.scss'

interface VehicleQuickFindInterface {
  onVehicleSelected: (vehicle?: VehicleSuggestionData) => void
  vehicleLookup: boolean
  vehicleInputText?: string
  showPlaceHolder?: boolean
  showDropDownIndicator?: boolean
  label?: string
}

const getVehicleSuggestions = async (
  query: string
): Promise<VehicleSuggestionData[]> => {
  try {
    const response =
      await VehicleSaytServiceProvider.getVehicleSuggestion(query)
    return response?.Data?.vehicles || []
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching vehicle suggestions:', error)
    throw error
  }
}

const CustomLoadingIndicator = () => (
  <div className={styles.loading_wrapper}>
    <Spinner className={styles.spinner} />
  </div>
)

const CustomDropdownIndicator: React.ComponentType<
  DropdownIndicatorProps<
    VehicleSuggestionData,
    false,
    GroupBase<VehicleSuggestionData>
  >
> = (
  props: React.PropsWithChildren<
    DropdownIndicatorProps<
      VehicleSuggestionData,
      false,
      GroupBase<VehicleSuggestionData>
    >
  >
) => {
  if (props.selectProps.inputValue || props.selectProps.isLoading) {
    return null
  }
  return (
    <components.DropdownIndicator {...props}>
      <span className="material-icons md-dark">search</span>
    </components.DropdownIndicator>
  )
}

export const VehicleQuickFind = observer(
  (props: VehicleQuickFindInterface): ReactElement => {
    const { uiStore } = StoreInstances
    const { onVehicleSelected, vehicleLookup, vehicleInputText } = props
    const showPlaceHolder = props.showPlaceHolder ?? true
    const enterVehicletext = Translate('enterVehicle')
    const noOptionsText = Translate('noOptions')
    const placeholder = showPlaceHolder ? enterVehicletext : ''
    const showDropDownIndicator = props.showDropDownIndicator ?? true
    const dropdownIndicator = showDropDownIndicator
      ? CustomDropdownIndicator
      : null
    const label = props.label ?? 'vehicleQuickFind'
    const vehicleWidgetStore = useVehicleWidgetStore()
    const { resetStore } = usePartsCatalog()
    const { clearAnswers: clearSpecificConditionAnswers } =
      useSpecificConditionStore()

    const [inputValue, setInputValue] = useState(vehicleInputText ?? '')
    const [options, setOptions] = useState<VehicleSuggestionData[]>([])
    const [loading, setLoading] = useState(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOptions = useCallback(
      debounce(async (inputValue) => {
        setLoading(true)
        const newOptions = await getVehicleSuggestions(inputValue)
        setOptions(newOptions)
        setLoading(false)
      }, 700),
      []
    )

    useEffect(() => {
      if (inputValue) {
        loadOptions(inputValue)
      }
    }, [inputValue, loadOptions])

    const onInputChange = (newValue: string, actionMeta?: InputActionMeta) => {
      if (!(actionMeta?.action === 'set-value' && newValue === '')) {
        setInputValue(newValue)
      }
    }

    const onVehicleSelect = async (vehicle) => {
      if (vehicleLookup) {
        const { Year, Make, Model, Engine, SubModel, catId, regionIds } =
          vehicle

        try {
          setLoading(true)
          const ymmeParts = [Year, Make, Model]
          if (SubModel) {
            ymmeParts.push(SubModel)
          }
          ymmeParts.push(Engine)
          const suggestedYMME = ymmeParts.join(',')
          const decodedVehicle =
            await VehicleServiceProvider.getDecodedAndValidatedYMME(
              {
                ymme: suggestedYMME,
              },
              catId[0],
              regionIds
            )

          if (decodedVehicle && isVehicleComplete(decodedVehicle)) {
            await vehicleWidgetStore.setCurrentVehicle(decodedVehicle)
            resetStore()
            clearSpecificConditionAnswers()
            onVehicleSelected()
          } else {
            throw new Error(
              'An error occurred while trying to retrieve the vehicle information.'
            )
          }
        } catch (_error) {
          uiStore.displayErrorNotification('vehicleInfoRetrievalError')
        }
      } else {
        onVehicleSelected(vehicle)
      }
    }

    const isCompleteVehicle = (vehicle: VehicleSuggestionData): boolean => {
      return !!(
        vehicle?.Engine &&
        vehicle?.Make &&
        vehicle?.Model &&
        vehicle?.Year
      )
    }

    return (
      <>
        <Label>
          <I18n name={label} />
        </Label>
        <Select
          components={{
            LoadingIndicator: CustomLoadingIndicator,
            DropdownIndicator: dropdownIndicator,
          }}
          classNames={{
            control: (state) => {
              let style = state.isFocused
                ? `${styles.controlClass} ${styles.focused}`
                : styles.controlClass
              if (!vehicleLookup) {
                style = `${style} ${styles.advancedSearch}`
              }
              return style
            },
            dropdownIndicator: () => styles.dropdownIndicator_class,
            indicatorSeparator: () => styles.indicatorSeparator_class,
            option: () => styles.option_class,
            clearIndicator: () => styles.clearIndicator_class,
          }}
          options={options}
          filterOption={() => true}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => JSON.stringify(option)}
          placeholder={placeholder}
          isLoading={loading}
          onChange={(value) => {
            if (isCompleteVehicle(value)) {
              onVehicleSelect(value)
            } else {
              onInputChange(value?.name)
            }
          }}
          closeMenuOnSelect={isCompleteVehicle(options[0])} // We should show the menu options till we select a complete vehicle. If options[0] is a complete vehicle, then all other options are also complete vehicles.
          inputValue={inputValue}
          onInputChange={onInputChange}
          isClearable={true}
          escapeClearsValue={true}
          noOptionsMessage={() => noOptionsText}
        />
      </>
    )
  }
)

const Label = styled.div`
  font-weight: bold;
  font-family: Roboto;
  font-size: 14px;
  margin: 4px;
  text-align: left;
`
